/**
 * GovernanceGraphMotif
 *
 * Decorative/functional graph visualization for governance relationships.
 * Renders nodes and edges with type-based styling.
 */
import React, { useMemo } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { governanceTokens } from './design-tokens';

export type NodeType = 'Policy' | 'Control' | 'Evidence' | 'Framework' | 'Pipeline' | 'Service';

export interface GraphNode {
  id: string;
  type: NodeType;
  label: string;
  status?: 'active' | 'warning' | 'error' | 'inactive';
}

export interface GraphEdge {
  from: string;
  to: string;
  type?: string;
}

export interface GovernanceGraphMotifProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  onNodeClick?: (nodeId: string) => void;
  interactive?: boolean;
}

const nodeStatusColors: Record<string, string> = {
  active: governanceTokens.colorGovernancePrimary,
  warning: governanceTokens.colorStatusWarning,
  error: governanceTokens.colorStatusError,
  // #475569 (slate grey) is part of the governance diagram's dark-surface accent
  // scheme, not the brand palette — no token represents it, left as-is.
  inactive: '#475569',
};

const nodeShapes: Record<NodeType, string> = {
  Policy: 'circle',
  Control: 'square',
  Evidence: 'diamond',
  Framework: 'hexagon',
  Pipeline: 'rectangle',
  Service: 'circle',
};

const useStyles = makeStyles({
  container: {
    position: 'relative',
    width: '100%',
    minHeight: '200px',
    overflow: 'hidden',
  },
  svg: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'none',
  },
  nodesContainer: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingVerticalL,
    justifyContent: 'center',
    alignItems: 'center',
  },
  node: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalS,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  nodeInteractive: {
    cursor: 'pointer',
    ':hover': {
      transform: 'scale(1.08)',
    },
  },
  nodeShape: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForegroundStaticInverted,
    transition: 'box-shadow 0.2s ease',
  },
  nodeLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
    maxWidth: '80px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

function getTypeAbbrev(type: NodeType): string {
  switch (type) {
    case 'Policy': return 'PO';
    case 'Control': return 'CT';
    case 'Evidence': return 'EV';
    case 'Framework': return 'FW';
    case 'Pipeline': return 'PL';
    case 'Service': return 'SV';
  }
}

export const GovernanceGraphMotif: React.FC<GovernanceGraphMotifProps> = ({
  nodes,
  edges,
  onNodeClick,
  interactive = false,
}) => {
  const styles = useStyles();

  const nodePositions = useMemo(() => {
    // Simple circular layout
    const cx = 50;
    const cy = 50;
    const r = 35;
    return nodes.reduce<Record<string, { x: number; y: number }>>((acc, node, i) => {
      const angle = (2 * Math.PI * i) / nodes.length - Math.PI / 2;
      acc[node.id] = {
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle),
      };
      return acc;
    }, {});
  }, [nodes]);

  return (
    <div className={styles.container} style={{ height: `${Math.max(200, nodes.length * 30)}px` }}>
      {/* Edge lines */}
      <svg className={styles.svg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        {edges.map((edge, i) => {
          const from = nodePositions[edge.from];
          const to = nodePositions[edge.to];
          if (!from || !to) return null;
          return (
            <line
              key={i}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={governanceTokens.colorGovernanceDivider}
              strokeWidth="0.4"
            />
          );
        })}
      </svg>

      {/* Nodes overlay using absolute positioning */}
      <svg className={styles.svg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        {nodes.map((node) => {
          const pos = nodePositions[node.id];
          if (!pos) return null;
          const color = nodeStatusColors[node.status ?? 'active'] ?? governanceTokens.colorGovernanceSecondary;
          const shape = nodeShapes[node.type];
          const r = shape === 'rectangle' ? 4 : 3.5;

          return (
            <g
              key={node.id}
              onClick={interactive && onNodeClick ? () => onNodeClick(node.id) : undefined}
              style={{ cursor: interactive ? 'pointer' : 'default' }}
            >
              {shape === 'circle' || shape === 'service' ? (
                <circle cx={pos.x} cy={pos.y} r={r} fill={color} />
              ) : shape === 'hexagon' ? (
                <polygon
                  points={hexPoints(pos.x, pos.y, r)}
                  fill={color}
                />
              ) : (
                <rect
                  x={pos.x - r}
                  y={pos.y - r}
                  width={r * 2}
                  height={r * 2}
                  rx={shape === 'diamond' ? 0 : 1}
                  fill={color}
                  transform={shape === 'diamond' ? `rotate(45 ${pos.x} ${pos.y})` : undefined}
                />
              )}
              <text
                x={pos.x}
                y={pos.y + 0.8}
                textAnchor="middle"
                fill={tokens.colorNeutralForegroundStaticInverted}
                fontSize="2"
                fontWeight="bold"
              >
                {getTypeAbbrev(node.type)}
              </text>
              <text
                x={pos.x}
                y={pos.y + r + 3}
                textAnchor="middle"
                fill={governanceTokens.colorGovernanceTextSecondary}
                fontSize="2.2"
              >
                {node.label.length > 12 ? node.label.slice(0, 11) + '...' : node.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

function hexPoints(cx: number, cy: number, r: number): string {
  return Array.from({ length: 6 })
    .map((_, i) => {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
    })
    .join(' ');
}
